// src/pages/projects/SowImportWizard.jsx
// Smart-but-faithful PDF import for existing Scope of Work documents.
// Workflow: pick template → upload PDF → review/adjust structure → save.
//
// Important: the parser does NOT rewrite or fuzzy-match the PDF content —
// every bullet from the PDF lands in the saved scope as a custom
// deliverable with its original wording. The review step is purely
// structural: move misassigned bullets to the correct stage, edit
// imperfect extraction, or drop lines that aren't deliverables.

import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Upload, FileText, ChevronLeft, ChevronRight, Check,
  Plus, Trash2, Loader, CheckCircle, Clipboard,
} from 'lucide-react';
import {
  BrioModal,
  BrioButton,
  BrioSelect,
  BrioInput,
  BrioBadge,
  BrioAlert,
} from '../../components/BrioComponents';
import { parseSOWFile, parseSOWFromHtml } from '../../utils/sowParser';
import {
  DEFAULT_SCOPE_TEMPLATE,
  SCOPE_STATUSES,
} from '../../utils/contractScopeHelpers';

const STEP_ORDER = ['setup', 'review', 'save'];
const STEP_LABELS = { setup: 'Upload', review: 'Review', save: 'Save' };

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

let _idCounter = 0;
function makeId(prefix) {
  _idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${_idCounter}`;
}

// Build the editable draft from a parser result. Every bullet from the PDF
// lands as a custom deliverable with verbatim text — no template-side
// deliverable wording is ever introduced. Prose lines are joined as the
// stage introduction draft so they too are preserved verbatim.
function buildDraftFromParse(parsed, template) {
  // Interior-design section bullets become the saved scope's top-level
  // interiorDeliverables array (the "Summary of Interior Deliverables"
  // in the rendered SOW). When the PDF dictates the list, we replace
  // the template's 17 default labels — content stays verbatim.
  // Imported items default to the Warm Shell section; users can re-assign
  // them after import.
  const interiorFromPdf = parsed.interiorBullets.map(b => ({
    id: makeId('intd'),
    label: b.text,
    included: true,
    section: 'warm-shell',
    sourceIndex: b.index,
  }));
  const useInteriorFromPdf = interiorFromPdf.length > 0;

  return {
    title: parsed.fileName.replace(/\.[^/.]+$/, '') || 'Imported Scope of Work',
    // Top-of-document boilerplate from the PDF preamble (verbatim) if any
    // unassigned prose was found, otherwise leave blank — we deliberately
    // do NOT inject the template's boilerplate here.
    templateIntroduction: parsed.unassignedProse.map(p => p.text).join('\n'),
    // Toggle Warm Shell on whenever we have items from the PDF; FF&E
    // defaults off because we don't yet know which items belong there.
    interiorWarmShellSelected: useInteriorFromPdf,
    interiorFfeSelected: false,
    interiorDetected: parsed.interiorDetected,
    // Verbatim prose from the PDF's interior section (text that wasn't
    // a bullet). Falls back to the template's interior introduction if
    // the PDF didn't carry one.
    interiorIntroduction: parsed.interiorProse && parsed.interiorProse.length
      ? parsed.interiorProse.map(p => p.text).join('\n')
      : (template.interiorIntroduction || ''),
    // If the PDF carried an Interior Design section, use those verbatim
    // bullets; otherwise carry the template's defaults (left unselected
    // so they don't sneak template wording into a PDF that didn't say
    // them).
    interiorDeliverables: useInteriorFromPdf
      ? interiorFromPdf
      : (template.interiorDeliverables || []).map(d => ({
        ...d,
        included: false,
        section: d.section || 'warm-shell',
      })),
    stages: parsed.stages.map(parsedStage => {
      const tplStage = template.stages.find(s => s.key === parsedStage.key);
      return {
        key: parsedStage.key,
        stageId: parsedStage.stageId,
        name: parsedStage.name,
        fullName: parsedStage.fullName,
        // A stage is selected by default whenever the PDF actually had
        // content for it. Empty template stages stay unselected.
        selected: parsedStage.sectionDetected,
        sectionDetected: parsedStage.sectionDetected,
        descriptionReviewed: false,
        description: tplStage?.description || '',
        // Prose BEFORE the last deliverable, with sub-headers
        // (Programming, Pre-Concept Design, …) wrapped in **bold**.
        introduction: parsedStage.introductionDraft || '',
        // Prose AFTER the last deliverable in the source document
        // (e.g. "Any additional revisions during the Concept Design
        // Phase shall be considered Additional Services."). Rendered
        // verbatim under the deliverable list.
        notes: parsedStage.notesDraft || '',
        deliverables: {
          // Standard / optional deliverables stay empty: the saved scope
          // reflects exactly what the PDF said, not what the template
          // would have proposed.
          standard: [],
          optional: [],
          // Every bullet from the PDF, verbatim, with a stable id so it
          // appears as a Timelines row once the scope is confirmed.
          custom: parsedStage.bulletLines.map(b => ({
            id: makeId('imp'),
            label: b.text,
            type: 'custom',
            included: true,
            sourceIndex: b.index,
          })),
        },
      };
    }),
    // Bullet lines that didn't fall under any detected stage. The review
    // UI surfaces these so the user can either move them to a stage or
    // drop them.
    unassignedBullets: parsed.unassignedBullets.map(b => ({
      id: makeId('un'),
      label: b.text,
      sourceIndex: b.index,
    })),
  };
}

// Final shape persisted to app_settings.contract_scopes.
function buildScopeFromDraft(draft, projectId, template) {
  const now = new Date().toISOString();
  return {
    id: makeId('scope'),
    projectId: projectId || null,
    title: draft.title || 'Imported Scope of Work',
    templateId: template.id,
    version: 1,
    status: SCOPE_STATUSES.DRAFT,
    isActiveVersion: true,
    importedFromPdf: true,
    templateIntroduction: draft.templateIntroduction || '',
    interiorWarmShellSelected: !!draft.interiorWarmShellSelected,
    interiorFfeSelected: !!draft.interiorFfeSelected,
    interiorIntroduction: draft.interiorIntroduction || '',
    interiorDeliverables: draft.interiorDeliverables.map(d => ({
      ...d,
      section: d.section || 'warm-shell',
    })),
    stages: draft.stages.map(s => ({
      key: s.key,
      stageId: s.stageId,
      name: s.name,
      fullName: s.fullName,
      selected: s.selected,
      descriptionReviewed: s.descriptionReviewed,
      description: s.description,
      introduction: s.introduction,
      notes: s.notes,
      deliverables: {
        standard: [],
        optional: [],
        custom: s.deliverables.custom
          .filter(d => d.included)
          .map(d => {
            // Strip wizard-only fields so the persisted shape matches
            // createContractScope's output.
            const { sourceIndex, ...rest } = d; // eslint-disable-line no-unused-vars
            return rest;
          }),
      },
    })),
    notes: '',
    negotiationNotes: '',
    createdAt: now,
    updatedAt: now,
    confirmedAt: null,
    confirmedBy: null,
  };
}

// ----------------------------------------------------------------------------
// Step indicator
// ----------------------------------------------------------------------------

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      {STEP_ORDER.map((s, i) => {
        const idx = STEP_ORDER.indexOf(step);
        const active = i === idx;
        const done = i < idx;
        return (
          <React.Fragment key={s}>
            <div
              className="flex items-center gap-2"
              style={{
                color: active ? 'var(--brio-text-primary)' : done ? 'var(--brio-text-secondary)' : 'var(--brio-text-muted)',
                fontSize: 'var(--brio-font-size-s)',
                fontWeight: active ? 600 : 400,
              }}
            >
              <span
                className="flex items-center justify-center"
                style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: active ? 'var(--brio-gold)' : done ? 'var(--brio-green)' : 'var(--brio-grey-light)',
                  color: active || done ? '#fff' : 'var(--brio-text-muted)',
                  fontSize: 12, fontWeight: 600,
                }}
              >
                {done ? <Check size={12} /> : i + 1}
              </span>
              {STEP_LABELS[s]}
            </div>
            {i < STEP_ORDER.length - 1 && (
              <span style={{ flex: '0 0 24px', height: 1, background: 'var(--brio-border)' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Setup step
// ----------------------------------------------------------------------------

function PasteSection({ onSubmit, parsing, disabled }) {
  // contentEditable lets the browser keep the source app's HTML
  // representation when the user pastes (Pages, Word for the Web,
  // Google Docs, etc. all hand the OS clipboard a sanitised
  // <ol>/<ul>/<li>/<p> structure that we can walk just like the
  // mammoth output for .docx). Ref-based reads avoid
  // dangerouslySetInnerHTML.
  const editorRef = useRef(null);
  const [hasContent, setHasContent] = useState(false);

  const handleSubmit = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML || '';
    if (html.trim()) onSubmit(html);
  };

  const handleClear = () => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = '';
    setHasContent(false);
  };

  return (
    <div>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={() => {
          const t = (editorRef.current?.textContent || '').trim();
          setHasContent(t.length > 0);
        }}
        style={{
          minHeight: 140,
          maxHeight: 280,
          overflowY: 'auto',
          border: '1px solid var(--brio-border)',
          borderRadius: '6px',
          padding: 10,
          fontSize: 'var(--brio-font-size-base)',
          lineHeight: 1.5,
          background: 'var(--brio-white)',
          outline: 'none',
          opacity: disabled ? 0.6 : 1,
        }}
      />
      <div className="flex items-center gap-2 mt-2">
        <BrioButton variant="primary" onClick={handleSubmit} disabled={!hasContent || parsing || disabled}>
          {parsing ? <Loader size={14} className="animate-spin" /> : <Clipboard size={14} />}
          Parse pasted content
        </BrioButton>
        {hasContent && (
          <BrioButton variant="secondary" onClick={handleClear} disabled={parsing || disabled}>
            Clear
          </BrioButton>
        )}
      </div>
    </div>
  );
}

function SetupStep({
  templateId, onTemplateChange, confirmedTemplates,
  file, parsing, parseError, onPickFile, onPasteHtml,
}) {
  const fileInputRef = useRef(null);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
          Match against template
        </label>
        <BrioSelect value={templateId} onChange={e => onTemplateChange(e.target.value)} style={{ maxWidth: 360 }}>
          <option value="">BRIO Standard (default)</option>
          {confirmedTemplates.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </BrioSelect>
        <p className="mt-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
          The template's stage list is used only as a structural anchor — to decide which detected section in the source belongs to which stage. The wording of every bullet and paragraph is kept verbatim.
        </p>
      </div>

      <div>
        <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
          Scope of Work file
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) onPickFile(f);
          }}
        />
        <div
          className="flex items-center gap-3 p-4"
          style={{
            border: '1px dashed var(--brio-border)',
            borderRadius: '6px',
            background: 'var(--brio-grey-light)',
          }}
        >
          {file ? (
            <>
              <FileText size={20} style={{ color: 'var(--brio-gold)' }} />
              <div className="flex-1 min-w-0">
                <div style={{ fontWeight: 500 }}>{file.name}</div>
                <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <BrioButton variant="secondary" onClick={() => fileInputRef.current?.click()}>Replace</BrioButton>
            </>
          ) : (
            <>
              <Upload size={20} style={{ color: 'var(--brio-text-muted)' }} />
              <div className="flex-1">
                <div>Choose a PDF, Word (.docx), Excel, or CSV file</div>
                <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
                  Word (.docx) gives the cleanest extraction. PDFs work but can scramble layout. Legacy .doc and Apple .pages aren't supported as files — use the paste option below for .pages.
                </div>
              </div>
              <BrioButton variant="primary" onClick={() => fileInputRef.current?.click()}>
                <Upload size={14} /> Pick file
              </BrioButton>
            </>
          )}
        </div>
        {parsing && (
          <div className="flex items-center gap-2 mt-2" style={{ color: 'var(--brio-text-secondary)', fontSize: 'var(--brio-font-size-s)' }}>
            <Loader size={14} className="animate-spin" /> Parsing document…
          </div>
        )}
        {parseError && (
          <BrioAlert label="Parse error" className="mt-2">
            {parseError}
          </BrioAlert>
        )}
      </div>

      <div className="flex items-center gap-2" style={{ color: 'var(--brio-text-muted)', fontSize: 'var(--brio-font-size-s)' }}>
        <span style={{ flex: 1, height: 1, background: 'var(--brio-border)' }} />
        <span>or paste from clipboard</span>
        <span style={{ flex: 1, height: 1, background: 'var(--brio-border)' }} />
      </div>

      <div>
        <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
          Paste content (best for Apple Pages)
        </label>
        <p className="mb-2" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
          In Pages, select the scope-of-work content (Cmd+A inside the section) and copy. Click into the box below and paste (Cmd+V). Numbered lists, bullet lists, and headings are preserved through the clipboard — Pages's .docx export drops them, which is why direct paste works better.
        </p>
        <PasteSection onSubmit={onPasteHtml} parsing={parsing} disabled={!!file} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Review step (side-by-side)
// ----------------------------------------------------------------------------

function ExtractedTextPanel({ parsed }) {
  const groups = useMemo(() => {
    if (!parsed) return [];
    const byStage = new Map();
    byStage.set(-1, []); // preamble
    byStage.set(-2, []); // interior deliverables section
    parsed.stages.forEach((_, idx) => byStage.set(idx, []));
    parsed.sectionByLine.forEach((idx, lineIdx) => {
      byStage.get(idx).push({ lineIdx, text: parsed.lines[lineIdx] });
    });
    return [
      { label: 'Preamble / unassigned', stageKey: null, lines: byStage.get(-1) },
      { label: 'Interior Design (summary of interior deliverables)', stageKey: 'interior', lines: byStage.get(-2) },
      ...parsed.stages.map((s, i) => ({
        label: `${s.name} — ${s.fullName}`,
        stageKey: s.key,
        lines: byStage.get(i),
      })),
    ];
  }, [parsed]);

  return (
    <div
      style={{
        background: 'var(--brio-grey-light)',
        borderRadius: '6px',
        padding: '8px',
        height: '100%',
        overflowY: 'auto',
        fontSize: 'var(--brio-font-size-s)',
      }}
    >
      <div className="font-semibold uppercase tracking-wide mb-2" style={{ fontSize: 11, color: 'var(--brio-text-muted)' }}>
        Extracted text · {parsed.totalLines} lines · {parsed.fileName}
      </div>
      {groups.map(g => (
        g.lines.length === 0 ? null : (
          <div key={g.label} className="mb-3">
            <div
              className="font-semibold mb-1"
              style={{
                fontSize: 'var(--brio-font-size-s)',
                color: g.stageKey ? 'var(--brio-text-primary)' : 'var(--brio-text-muted)',
                borderBottom: '1px solid var(--brio-border)',
                paddingBottom: 2,
              }}
            >
              {g.label} <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>({g.lines.length})</span>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: 'var(--brio-text-secondary)' }}>
              {g.lines.map((l, i) => (
                <div key={i}>{l.text}</div>
              ))}
            </div>
          </div>
        )
      ))}
    </div>
  );
}

// One row per imported deliverable. Verbatim text is editable in case
// extraction merged or split a line incorrectly. The "Move to" picker
// is the primary structural-fix affordance the user has.
function ImportedDeliverableRow({ d, stageOptions, currentStageKey, onToggle, onRename, onMove, onRemove }) {
  return (
    <div className="flex items-center gap-2" style={{ minHeight: 30 }}>
      <input
        type="checkbox"
        checked={!!d.included}
        onChange={onToggle}
        className="brio-checkbox"
        title="Include this deliverable in the saved scope"
      />
      <input
        type="text"
        value={d.label}
        onChange={e => onRename(e.target.value)}
        className="brio-input"
        style={{ flex: 1, fontSize: 'var(--brio-font-size-s)', padding: '2px 6px' }}
      />
      {onMove && (
        <BrioSelect
          value={currentStageKey}
          onChange={e => onMove(e.target.value)}
          style={{ fontSize: 'var(--brio-font-size-s)', maxWidth: 130 }}
          title="Move to a different stage"
        >
          {stageOptions.map(opt => (
            <option key={opt.key} value={opt.key}>{opt.name}</option>
          ))}
        </BrioSelect>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="brio-icon-btn"
        aria-label="Remove"
        style={{ color: 'var(--brio-red)' }}
        title="Remove this line"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function StageReviewBlock({
  stage,
  stageOptions,
  onToggleStage,
  onToggleDeliverable,
  onRenameDeliverable,
  onMoveDeliverable,
  onRemoveDeliverable,
  onAddCustom,
  onChangeIntro,
  onChangeNotes,
  initiallyExpanded,
}) {
  const [expanded, setExpanded] = useState(!!initiallyExpanded);
  const [customDraft, setCustomDraft] = useState('');
  const count = stage.deliverables.custom.length;
  const includedCount = stage.deliverables.custom.filter(d => d.included).length;

  return (
    <div
      style={{
        border: '1px solid var(--brio-border)',
        borderRadius: '6px',
        marginBottom: '8px',
        background: 'var(--brio-white)',
      }}
    >
      <div
        className="flex items-center gap-2 p-2"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(v => !v)}
      >
        <input
          type="checkbox"
          checked={!!stage.selected}
          onChange={e => { e.stopPropagation(); onToggleStage(); }}
          onClick={e => e.stopPropagation()}
          className="brio-checkbox"
        />
        <div className="flex-1">
          <div style={{ fontWeight: 600 }}>
            {stage.name} <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>· {stage.fullName}</span>
          </div>
        </div>
        <BrioBadge variant={stage.sectionDetected ? 'success' : 'neutral'}>
          {stage.sectionDetected ? 'section found' : 'not found in PDF'}
        </BrioBadge>
        <BrioBadge variant={count > 0 ? 'info' : 'neutral'}>
          {includedCount}/{count} deliverable{count === 1 ? '' : 's'}
        </BrioBadge>
        {expanded ? <ChevronLeft size={14} style={{ transform: 'rotate(-90deg)' }} /> : <ChevronRight size={14} />}
      </div>

      {expanded && (
        <div className="p-3 pt-0">
          <div className="mb-2">
            <div className="font-semibold uppercase tracking-wide mb-1" style={{ fontSize: 11, color: 'var(--brio-text-muted)' }}>
              Deliverables (verbatim from PDF)
            </div>
            {stage.deliverables.custom.length === 0 ? (
              <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
                No deliverables detected for this stage in the PDF. Add any missing items below.
              </div>
            ) : (
              stage.deliverables.custom.map(d => (
                <ImportedDeliverableRow
                  key={d.id}
                  d={d}
                  stageOptions={stageOptions}
                  currentStageKey={stage.key}
                  onToggle={() => onToggleDeliverable(d.id)}
                  onRename={(label) => onRenameDeliverable(d.id, label)}
                  onMove={(toKey) => onMoveDeliverable(d.id, toKey)}
                  onRemove={() => onRemoveDeliverable(d.id)}
                />
              ))
            )}
          </div>

          <div className="flex items-center gap-2 mb-3">
            <BrioInput
              placeholder="Add missed deliverable…"
              value={customDraft}
              onChange={e => setCustomDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && customDraft.trim()) {
                  onAddCustom(customDraft.trim());
                  setCustomDraft('');
                }
              }}
              style={{ flex: 1, fontSize: 'var(--brio-font-size-s)' }}
            />
            <BrioButton
              variant="secondary"
              onClick={() => {
                if (customDraft.trim()) {
                  onAddCustom(customDraft.trim());
                  setCustomDraft('');
                }
              }}
            >
              <Plus size={12} /> Add
            </BrioButton>
          </div>

          <div className="mb-2">
            <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 11, color: 'var(--brio-text-muted)' }}>
              Stage introduction (verbatim prose from PDF)
            </label>
            <textarea
              value={stage.introduction}
              onChange={e => onChangeIntro(e.target.value)}
              rows={4}
              className="brio-input"
              style={{ width: '100%', fontSize: 'var(--brio-font-size-s)', padding: 6 }}
              placeholder="Prose lines under this stage in the PDF, joined verbatim. Trim or rearrange as needed."
            />
          </div>

          <div>
            <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 11, color: 'var(--brio-text-muted)' }}>
              Notes
            </label>
            <textarea
              value={stage.notes}
              onChange={e => onChangeNotes(e.target.value)}
              rows={2}
              className="brio-input"
              style={{ width: '100%', fontSize: 'var(--brio-font-size-s)', padding: 6 }}
              placeholder="Any notes shown after the deliverables (e.g. revision allowance). Empty by default."
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Top-level "Summary of Interior Deliverables" — populated verbatim from
// the PDF's INTERIOR DESIGN section when one was detected. Lives outside
// the per-stage list because the BRIO scope template carries it as a
// separate top-level array on the saved scope.
const WIZARD_INTERIOR_SECTIONS = [
  { key: 'warm-shell', label: 'Warm Shell' },
  { key: 'ffe', label: 'FF&E' },
];

function InteriorDeliverablesBlock({
  warmShellSelected,
  ffeSelected,
  onToggleWarmShell,
  onToggleFfe,
  items,
  detected,
  introduction,
  onChangeIntroduction,
  onToggleItem,
  onRenameItem,
  onMoveItemSection,
  onRemoveItem,
  onAddItem,
}) {
  const anySelected = warmShellSelected || ffeSelected;
  const [expanded, setExpanded] = useState(detected || items.some(i => i.included) || !!introduction);
  const [draftBySection, setDraftBySection] = useState({});
  if (items.length === 0 && !detected && !introduction) return null;

  const sectionSelected = { 'warm-shell': warmShellSelected, 'ffe': ffeSelected };
  const itemsBySection = WIZARD_INTERIOR_SECTIONS.reduce((acc, s) => {
    acc[s.key] = items.filter(d => (d.section || 'warm-shell') === s.key);
    return acc;
  }, {});
  const includedCount = items.filter(d => d.included && sectionSelected[d.section || 'warm-shell']).length;
  const eligibleCount = items.filter(d => sectionSelected[d.section || 'warm-shell']).length;

  return (
    <div
      style={{
        border: '1px solid var(--brio-border)',
        borderRadius: '6px',
        marginBottom: '8px',
        background: 'var(--brio-white)',
      }}
    >
      <div
        className="flex items-center gap-2 p-2"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex-1">
          <div style={{ fontWeight: 600 }}>
            Summary of Interior Deliverables
          </div>
          <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
            Pick Warm Shell, FF&amp;E, or both. Imported items default to Warm Shell — move them as needed.
          </div>
        </div>
        <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()} style={{ fontSize: 'var(--brio-font-size-s)' }}>
          <input
            type="checkbox"
            checked={!!warmShellSelected}
            onChange={() => onToggleWarmShell && onToggleWarmShell()}
            className="brio-checkbox"
          />
          Warm Shell
        </label>
        <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()} style={{ fontSize: 'var(--brio-font-size-s)' }}>
          <input
            type="checkbox"
            checked={!!ffeSelected}
            onChange={() => onToggleFfe && onToggleFfe()}
            className="brio-checkbox"
          />
          FF&amp;E
        </label>
        <BrioBadge variant={detected ? 'success' : 'neutral'}>
          {detected ? 'section found' : 'not in PDF'}
        </BrioBadge>
        <BrioBadge variant={anySelected ? 'info' : 'neutral'}>
          {includedCount}/{eligibleCount}
        </BrioBadge>
        {expanded ? <ChevronLeft size={14} style={{ transform: 'rotate(-90deg)' }} /> : <ChevronRight size={14} />}
      </div>

      {expanded && (
        <div className="p-3 pt-0">
          <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 11, color: 'var(--brio-text-muted)' }}>
            Introduction (verbatim prose from PDF)
          </label>
          <textarea
            value={introduction || ''}
            onChange={e => onChangeIntroduction && onChangeIntroduction(e.target.value)}
            rows={Math.max(2, Math.min(8, ((introduction || '').match(/\n/g) || []).length + 2))}
            className="brio-input"
            style={{ width: '100%', fontSize: 'var(--brio-font-size-s)', padding: 6, marginBottom: 8 }}
            placeholder="Optional introduction shown above the interior deliverables list. Use **text** for bold sub-headers."
          />
          {WIZARD_INTERIOR_SECTIONS.map(section => {
            const sectionItems = itemsBySection[section.key];
            const otherKey = section.key === 'warm-shell' ? 'ffe' : 'warm-shell';
            const otherLabel = section.key === 'warm-shell' ? 'FF&E' : 'Warm Shell';
            const draft = draftBySection[section.key] || '';
            const sectionOn = sectionSelected[section.key];
            return (
              <div key={section.key} className="mb-2">
                <div className="font-semibold uppercase tracking-wide mb-1" style={{ fontSize: 11, color: sectionOn ? 'var(--brio-text-primary)' : 'var(--brio-text-muted)' }}>
                  {section.label}
                  <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--brio-text-muted)' }}>
                    ({sectionItems.filter(d => d.included).length}/{sectionItems.length})
                  </span>
                  {!sectionOn && (
                    <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--brio-text-muted)', fontStyle: 'italic', textTransform: 'none' }}>
                      — section not selected
                    </span>
                  )}
                </div>
                {sectionItems.length === 0 ? (
                  <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic', marginBottom: 4 }}>
                    No items in this section.
                  </div>
                ) : (
                  sectionItems.map(d => (
                    <div key={d.id} className="flex items-center gap-2" style={{ minHeight: 30 }}>
                      <input
                        type="checkbox"
                        checked={!!d.included}
                        onChange={() => onToggleItem(d.id)}
                        disabled={!sectionOn}
                        className="brio-checkbox"
                      />
                      <input
                        type="text"
                        value={d.label}
                        onChange={e => onRenameItem(d.id, e.target.value)}
                        className="brio-input"
                        style={{ flex: 1, fontSize: 'var(--brio-font-size-s)', padding: '2px 6px' }}
                      />
                      <button
                        type="button"
                        onClick={() => onMoveItemSection && onMoveItemSection(d.id, otherKey)}
                        className="brio-icon-btn"
                        title={`Move to ${otherLabel}`}
                        style={{ fontSize: 10, color: 'var(--brio-gold)', padding: '2px 6px' }}
                      >
                        → {otherLabel}
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveItem(d.id)}
                        className="brio-icon-btn"
                        aria-label="Remove"
                        style={{ color: 'var(--brio-red)' }}
                        title="Remove this line"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))
                )}
                <div className="flex items-center gap-2 mt-1">
                  <BrioInput
                    placeholder={`Add ${section.label} deliverable…`}
                    value={draft}
                    onChange={e => setDraftBySection(prev => ({ ...prev, [section.key]: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && draft.trim()) {
                        onAddItem(draft.trim(), section.key);
                        setDraftBySection(prev => ({ ...prev, [section.key]: '' }));
                      }
                    }}
                    style={{ flex: 1, fontSize: 'var(--brio-font-size-s)' }}
                  />
                  <BrioButton
                    variant="secondary"
                    onClick={() => {
                      if (draft.trim()) {
                        onAddItem(draft.trim(), section.key);
                        setDraftBySection(prev => ({ ...prev, [section.key]: '' }));
                      }
                    }}
                  >
                    <Plus size={12} /> Add
                  </BrioButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Bullet lines that didn't land in any detected stage section. The user
// either moves each to a stage or removes it. This is the main escape
// hatch when the PDF's headings don't match template stage names.
function UnassignedBlock({ items, stageOptions, onMove, onRemove }) {
  if (items.length === 0) return null;
  return (
    <div
      style={{
        border: '1px solid var(--brio-yellow)',
        borderRadius: '6px',
        marginBottom: '8px',
        background: 'rgba(236, 210, 136, 0.1)',
      }}
    >
      <div className="p-3">
        <div className="font-semibold mb-2" style={{ color: 'var(--brio-text-primary)' }}>
          Unassigned bullets ({items.length})
        </div>
        <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)', marginBottom: 8 }}>
          These lines look like deliverables but didn't fall under any recognised stage heading. Pick a stage to move each to, or remove it.
        </div>
        {items.map(item => (
          <div key={item.id} className="flex items-center gap-2 py-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>
            <span style={{ flex: 1, color: 'var(--brio-text-secondary)' }}>{item.label}</span>
            <BrioSelect
              value=""
              onChange={e => { if (e.target.value) onMove(item.id, e.target.value); }}
              style={{ fontSize: 'var(--brio-font-size-s)', maxWidth: 160 }}
            >
              <option value="">Move to stage…</option>
              {stageOptions.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.name}</option>
              ))}
            </BrioSelect>
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              className="brio-icon-btn"
              aria-label="Remove"
              style={{ color: 'var(--brio-red)' }}
              title="Drop this line"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewStep({ parsed, draft, setDraft }) {
  const updateStage = useCallback((key, updater) => {
    setDraft(prev => ({
      ...prev,
      stages: prev.stages.map(s => s.key === key ? updater(s) : s),
    }));
  }, [setDraft]);

  const stageOptions = useMemo(
    () => draft.stages.map(s => ({ key: s.key, name: s.name })),
    [draft.stages]
  );

  // Move a deliverable from its current stage to another. We auto-select
  // the destination stage so the moved item is included immediately.
  const handleMoveDeliverable = useCallback((fromKey, deliverableId, toKey) => {
    if (fromKey === toKey) return;
    setDraft(prev => {
      const fromStage = prev.stages.find(s => s.key === fromKey);
      if (!fromStage) return prev;
      const moving = fromStage.deliverables.custom.find(d => d.id === deliverableId);
      if (!moving) return prev;
      return {
        ...prev,
        stages: prev.stages.map(s => {
          if (s.key === fromKey) {
            return {
              ...s,
              deliverables: {
                ...s.deliverables,
                custom: s.deliverables.custom.filter(d => d.id !== deliverableId),
              },
            };
          }
          if (s.key === toKey) {
            return {
              ...s,
              selected: true,
              deliverables: {
                ...s.deliverables,
                custom: [...s.deliverables.custom, { ...moving }],
              },
            };
          }
          return s;
        }),
      };
    });
  }, [setDraft]);

  const handleMoveUnassigned = useCallback((unassignedId, toKey) => {
    setDraft(prev => {
      const item = prev.unassignedBullets.find(u => u.id === unassignedId);
      if (!item) return prev;
      return {
        ...prev,
        unassignedBullets: prev.unassignedBullets.filter(u => u.id !== unassignedId),
        stages: prev.stages.map(s => {
          if (s.key !== toKey) return s;
          return {
            ...s,
            selected: true,
            deliverables: {
              ...s.deliverables,
              custom: [
                ...s.deliverables.custom,
                { id: makeId('mv'), label: item.label, type: 'custom', included: true, sourceIndex: item.sourceIndex },
              ],
            },
          };
        }),
      };
    });
  }, [setDraft]);

  const handleRemoveUnassigned = useCallback((unassignedId) => {
    setDraft(prev => ({
      ...prev,
      unassignedBullets: prev.unassignedBullets.filter(u => u.id !== unassignedId),
    }));
  }, [setDraft]);

  // Interior-deliverables (top-level scope field, not per-stage).
  const interiorHandlers = useMemo(() => ({
    onToggleWarmShell: () => setDraft(prev => ({ ...prev, interiorWarmShellSelected: !prev.interiorWarmShellSelected })),
    onToggleFfe: () => setDraft(prev => ({ ...prev, interiorFfeSelected: !prev.interiorFfeSelected })),
    onChangeIntroduction: (text) => setDraft(prev => ({ ...prev, interiorIntroduction: text })),
    onToggleItem: (id) => setDraft(prev => ({
      ...prev,
      interiorDeliverables: prev.interiorDeliverables.map(d => d.id === id ? { ...d, included: !d.included } : d),
    })),
    onRenameItem: (id, label) => setDraft(prev => ({
      ...prev,
      interiorDeliverables: prev.interiorDeliverables.map(d => d.id === id ? { ...d, label } : d),
    })),
    onMoveItemSection: (id, toSection) => setDraft(prev => ({
      ...prev,
      interiorDeliverables: prev.interiorDeliverables.map(d => d.id === id ? { ...d, section: toSection } : d),
    })),
    onRemoveItem: (id) => setDraft(prev => ({
      ...prev,
      interiorDeliverables: prev.interiorDeliverables.filter(d => d.id !== id),
    })),
    onAddItem: (label, section) => setDraft(prev => {
      const sect = section || 'warm-shell';
      return {
        ...prev,
        interiorWarmShellSelected: sect === 'warm-shell' ? true : prev.interiorWarmShellSelected,
        interiorFfeSelected: sect === 'ffe' ? true : prev.interiorFfeSelected,
        interiorDeliverables: [
          ...prev.interiorDeliverables,
          { id: makeId('intd'), label, included: true, section: sect },
        ],
      };
    }),
  }), [setDraft]);

  const buildStageHandlers = useCallback((stage) => ({
    onToggleStage: () => updateStage(stage.key, s => ({ ...s, selected: !s.selected })),
    onToggleDeliverable: (id) => updateStage(stage.key, s => ({
      ...s,
      deliverables: {
        ...s.deliverables,
        custom: s.deliverables.custom.map(d => d.id === id ? { ...d, included: !d.included } : d),
      },
    })),
    onRenameDeliverable: (id, label) => updateStage(stage.key, s => ({
      ...s,
      deliverables: {
        ...s.deliverables,
        custom: s.deliverables.custom.map(d => d.id === id ? { ...d, label } : d),
      },
    })),
    onMoveDeliverable: (id, toKey) => handleMoveDeliverable(stage.key, id, toKey),
    onRemoveDeliverable: (id) => updateStage(stage.key, s => ({
      ...s,
      deliverables: {
        ...s.deliverables,
        custom: s.deliverables.custom.filter(d => d.id !== id),
      },
    })),
    onAddCustom: (label) => updateStage(stage.key, s => ({
      ...s,
      deliverables: {
        ...s.deliverables,
        custom: [
          ...s.deliverables.custom,
          { id: makeId('add'), label, type: 'custom', included: true },
        ],
      },
    })),
    onChangeIntro: (text) => updateStage(stage.key, s => ({ ...s, introduction: text })),
    onChangeNotes: (text) => updateStage(stage.key, s => ({ ...s, notes: text })),
  }), [updateStage, handleMoveDeliverable]);

  const [showAllStages, setShowAllStages] = useState(false);
  const visibleStages = showAllStages
    ? draft.stages
    : draft.stages.filter(s => s.selected || s.sectionDetected || s.deliverables.custom.length > 0);
  const hiddenCount = draft.stages.length - visibleStages.length;

  const totalSelected = draft.stages.filter(s => s.selected).length;

  return (
    <div className="flex flex-col gap-2" style={{ height: '100%' }}>
      <div className="flex items-center gap-3">
        <BrioBadge variant="info">
          {totalSelected} stage{totalSelected === 1 ? '' : 's'} selected
        </BrioBadge>
        {(draft.interiorWarmShellSelected || draft.interiorFfeSelected) && draft.interiorDeliverables.length > 0 && (() => {
          const eligible = draft.interiorDeliverables.filter(d => {
            const sect = d.section || 'warm-shell';
            return (sect === 'warm-shell' && draft.interiorWarmShellSelected)
              || (sect === 'ffe' && draft.interiorFfeSelected);
          });
          const includedCount = eligible.filter(d => d.included).length;
          return (
            <BrioBadge variant="info">
              {includedCount} interior deliverable{includedCount === 1 ? '' : 's'}
            </BrioBadge>
          );
        })()}
        {draft.unassignedBullets.length > 0 && (
          <BrioBadge variant="caution">
            {draft.unassignedBullets.length} unassigned
          </BrioBadge>
        )}
        {hiddenCount > 0 && !showAllStages && (
          <BrioButton variant="secondary" onClick={() => setShowAllStages(true)} style={{ fontSize: 'var(--brio-font-size-s)' }}>
            Show {hiddenCount} hidden stage{hiddenCount === 1 ? '' : 's'}
          </BrioButton>
        )}
        {showAllStages && (
          <BrioButton variant="secondary" onClick={() => setShowAllStages(false)} style={{ fontSize: 'var(--brio-font-size-s)' }}>
            Hide empty stages
          </BrioButton>
        )}
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', flex: 1, minHeight: 0 }}>
        <ExtractedTextPanel parsed={parsed} />

        <div style={{ overflowY: 'auto', height: '100%', paddingRight: 4 }}>
          <div className="font-semibold uppercase tracking-wide mb-2" style={{ fontSize: 11, color: 'var(--brio-text-muted)' }}>
            Imported scope · adjust the structure where needed
          </div>

          <UnassignedBlock
            items={draft.unassignedBullets}
            stageOptions={stageOptions}
            onMove={handleMoveUnassigned}
            onRemove={handleRemoveUnassigned}
          />

          <InteriorDeliverablesBlock
            warmShellSelected={!!draft.interiorWarmShellSelected}
            ffeSelected={!!draft.interiorFfeSelected}
            items={draft.interiorDeliverables}
            detected={!!draft.interiorDetected}
            introduction={draft.interiorIntroduction}
            {...interiorHandlers}
          />

          {visibleStages.map(stage => (
            <StageReviewBlock
              key={stage.key}
              stage={stage}
              stageOptions={stageOptions}
              initiallyExpanded={stage.selected}
              {...buildStageHandlers(stage)}
            />
          ))}
          {visibleStages.length === 0 && (
            <BrioAlert label="No stages auto-selected">
              The parser didn't recognise any stage sections in this PDF. Click "Show hidden stages" to pick stages manually, then move the unassigned bullets above into them.
            </BrioAlert>
          )}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Save step
// ----------------------------------------------------------------------------

function SaveStep({ draft, setDraft, projectId, setProjectId, activeProjects, defaultProjectId }) {
  const sortedProjects = useMemo(
    () => [...activeProjects].sort((a, b) =>
      (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || '')
    ),
    [activeProjects]
  );
  const totalSelected = draft.stages.filter(s => s.selected).length;
  const totalDeliverables = draft.stages.reduce((sum, s) => {
    if (!s.selected) return sum;
    return sum + s.deliverables.custom.filter(d => d.included).length;
  }, 0);
  const totalInterior = draft.interiorDeliverables.filter(d => {
    if (!d.included) return false;
    const sect = d.section || 'warm-shell';
    if (sect === 'warm-shell') return !!draft.interiorWarmShellSelected;
    if (sect === 'ffe') return !!draft.interiorFfeSelected;
    return false;
  }).length;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
          Scope title
        </label>
        <BrioInput
          value={draft.title}
          onChange={e => setDraft(prev => ({ ...prev, title: e.target.value }))}
          style={{ maxWidth: 480 }}
        />
      </div>

      <div>
        <label className="font-semibold uppercase tracking-wide mb-1 block" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-secondary)' }}>
          Attach to project
        </label>
        <BrioSelect value={projectId} onChange={e => setProjectId(e.target.value)} style={{ maxWidth: 480 }}>
          <option value="">— Save unattached (link later) —</option>
          {sortedProjects.map(p => (
            <option key={p.id} value={p.id}>
              {p.projectCode ? `${p.projectCode} — ${p.name}` : p.name}
            </option>
          ))}
        </BrioSelect>
        <p className="mt-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
          {projectId
            ? defaultProjectId && String(projectId) === String(defaultProjectId)
              ? 'Will appear in this project\'s Contracts & Scope page after save.'
              : 'Will appear under the selected project after save.'
            : 'Unattached scopes are saved as drafts and can be linked to a project later by editing the scope.'}
        </p>
      </div>

      <div className="p-3" style={{ background: 'var(--brio-grey-light)', borderRadius: '6px' }}>
        <div className="font-semibold uppercase tracking-wide mb-1" style={{ fontSize: 11, color: 'var(--brio-text-muted)' }}>
          Summary
        </div>
        <div className="flex items-center gap-3" style={{ fontSize: 'var(--brio-font-size-s)' }}>
          <span><strong>{totalSelected}</strong> stage{totalSelected === 1 ? '' : 's'} selected</span>
          <span style={{ color: 'var(--brio-text-muted)' }}>·</span>
          <span><strong>{totalDeliverables}</strong> deliverable{totalDeliverables === 1 ? '' : 's'} included</span>
          {totalInterior > 0 && (
            <>
              <span style={{ color: 'var(--brio-text-muted)' }}>·</span>
              <span><strong>{totalInterior}</strong> interior deliverable{totalInterior === 1 ? '' : 's'}</span>
            </>
          )}
          <span style={{ color: 'var(--brio-text-muted)' }}>·</span>
          <span>Status: <strong>Draft</strong></span>
        </div>
        <p className="mt-2" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
          Every deliverable is saved verbatim from the PDF and given a stable id, so it appears automatically as a row on the Timelines page once the scope is confirmed.
        </p>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main wizard
// ----------------------------------------------------------------------------

export default function SowImportWizard({
  onClose,
  onSave,
  activeProjects = [],
  confirmedTemplates = [],
  defaultProjectId = '',
}) {
  const [step, setStep] = useState('setup');
  const [templateId, setTemplateId] = useState('');
  const [file, setFile] = useState(null);
  // Source of truth for paste mode — keeping the raw HTML lets us re-parse
  // against a different template without asking the user to paste again.
  const [pastedHtml, setPastedHtml] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [draft, setDraft] = useState(null);
  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [saving, setSaving] = useState(false);

  const chosenTemplate = useMemo(() => {
    if (templateId) {
      const t = confirmedTemplates.find(ct => ct.id === templateId);
      if (t) return t;
    }
    return DEFAULT_SCOPE_TEMPLATE;
  }, [templateId, confirmedTemplates]);

  const handlePickFile = useCallback(async (f) => {
    setFile(f);
    setPastedHtml(null);
    setParseError(null);
    setParsing(true);
    try {
      const result = await parseSOWFile(f, chosenTemplate);
      setParsed(result);
      setDraft(buildDraftFromParse(result, chosenTemplate));
      setStep('review');
    } catch (err) {
      setParseError(err.message || 'Could not parse this file.');
    } finally {
      setParsing(false);
    }
  }, [chosenTemplate]);

  const handlePasteHtml = useCallback(async (html) => {
    setFile(null);
    setPastedHtml(html);
    setParseError(null);
    setParsing(true);
    try {
      const result = parseSOWFromHtml(html, 'Pasted from clipboard', chosenTemplate);
      setParsed(result);
      setDraft(buildDraftFromParse(result, chosenTemplate));
      setStep('review');
    } catch (err) {
      setParseError(err.message || 'Could not parse this content.');
    } finally {
      setParsing(false);
    }
  }, [chosenTemplate]);

  const handleTemplateChange = useCallback(async (id) => {
    setTemplateId(id);
    const nextTemplate = id
      ? confirmedTemplates.find(ct => ct.id === id) || DEFAULT_SCOPE_TEMPLATE
      : DEFAULT_SCOPE_TEMPLATE;
    if (!file && !pastedHtml) return;
    setParsing(true);
    setParseError(null);
    try {
      const result = file
        ? await parseSOWFile(file, nextTemplate)
        : parseSOWFromHtml(pastedHtml, 'Pasted from clipboard', nextTemplate);
      setParsed(result);
      setDraft(buildDraftFromParse(result, nextTemplate));
    } catch (err) {
      setParseError(err.message || 'Could not parse this content.');
      setParsed(null);
      setDraft(null);
      setStep('setup');
    } finally {
      setParsing(false);
    }
  }, [file, pastedHtml, confirmedTemplates]);

  const goBack = useCallback(() => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) setStep(STEP_ORDER[idx - 1]);
  }, [step]);

  const goNext = useCallback(() => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx < STEP_ORDER.length - 1) setStep(STEP_ORDER[idx + 1]);
  }, [step]);

  const mountedRef = useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);

  const handleSave = useCallback(() => {
    if (!draft) return;
    setSaving(true);
    const scope = buildScopeFromDraft(draft, projectId, chosenTemplate);
    Promise.resolve(onSave(scope)).finally(() => {
      if (mountedRef.current) setSaving(false);
    });
  }, [draft, projectId, chosenTemplate, onSave]);

  const canAdvanceFromSetup = (!!file || !!pastedHtml) && !!parsed && !parsing && !parseError;
  const canAdvanceFromReview = draft && draft.stages.some(s => s.selected);

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</BrioButton>
      {step !== 'setup' && (
        <BrioButton variant="secondary" onClick={goBack} disabled={saving}>
          <ChevronLeft size={14} /> Back
        </BrioButton>
      )}
      {step === 'setup' && (
        <BrioButton
          variant="primary"
          onClick={goNext}
          disabled={!canAdvanceFromSetup}
        >
          Next <ChevronRight size={14} />
        </BrioButton>
      )}
      {step === 'review' && (
        <BrioButton
          variant="primary"
          onClick={goNext}
          disabled={!canAdvanceFromReview}
        >
          Next <ChevronRight size={14} />
        </BrioButton>
      )}
      {step === 'save' && (
        <BrioButton variant="primary" onClick={handleSave} disabled={saving || !canAdvanceFromReview}>
          {saving ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          Save draft scope
        </BrioButton>
      )}
    </>
  );

  return (
    <BrioModal
      title="Import Scope of Work from PDF"
      onClose={saving ? undefined : onClose}
      width={step === 'review' ? 'xl' : 'lg'}
      footer={footer}
    >
      <div style={{ minHeight: step === 'review' ? 480 : 280, display: 'flex', flexDirection: 'column' }}>
        <StepIndicator step={step} />
        {step === 'setup' && (
          <SetupStep
            templateId={templateId}
            onTemplateChange={handleTemplateChange}
            confirmedTemplates={confirmedTemplates}
            file={file}
            parsing={parsing}
            parseError={parseError}
            onPickFile={handlePickFile}
            onPasteHtml={handlePasteHtml}
          />
        )}
        {step === 'review' && draft && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ReviewStep parsed={parsed} draft={draft} setDraft={setDraft} />
          </div>
        )}
        {step === 'save' && draft && (
          <SaveStep
            draft={draft}
            setDraft={setDraft}
            projectId={projectId}
            setProjectId={setProjectId}
            activeProjects={activeProjects}
            defaultProjectId={defaultProjectId}
          />
        )}
      </div>
    </BrioModal>
  );
}
